import { useTranslation } from 'react-i18next';
import { useState, useEffect, useMemo } from 'react';
import { Dices, Settings } from 'lucide-react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { useTitleValidation } from '../../hooks/useTitleValidation';
import { TitleSuggestionsDialog } from './TitleSuggestionsDialog';
import { localStorageService } from '../../services/localStorage';

interface ValidatedTitleInputProps {
  title: string;
  locationId: string | number | null;
  onTitleChange: (title: string) => void;
  hasError?: boolean;
  errorMessage?: string | null;
  excludeClimbId?: string | null;
  /**
   * The rest of the scope the name has to be unique in: its type, or — for a
   * board problem, which has no location — the board and the angle. Passed
   * straight through to useTitleValidation.
   */
  type?: 'boulder' | 'route' | 'board_climb' | null;
  boardId?: string | number | null;
  boardTypeId?: string | number | null;
  inclination?: number | null;
  /**
   * A control sat on the input's right-hand end — somewhere to put an action
   * that fills the field in, which belongs beside it rather than under it.
   */
  trailingAction?: React.ReactNode;
  /**
   * Put the whole naming business inside the field: a button that drops a name
   * in and one that opens the list, both sat in the input's right-hand end, and
   * no label or suggestion line around it. A setter naming a wall of problems
   * takes them one after another, and the page's own heading already says which
   * field this is — everything else around it is a row they scroll past thirty
   * times an afternoon.
   */
  compactSuggestions?: boolean;
  /**
   * The names to offer, and where they are managed. Left out, the field falls
   * back to the browser-local list the climb forms have always used; a gym
   * passes its own queue instead — see useGymNameSuggestions.
   */
  suggestions?: { names: string[]; onManage: () => void };
  /**
   * Told whenever the name in the field turns out to be taken, for a form that
   * has to hold its own save back rather than let the write fail. The field
   * says so on screen either way.
   */
  onExistsChange?: (exists: boolean) => void;
}

export function ValidatedTitleInput({
  title,
  locationId,
  onTitleChange,
  hasError = false,
  errorMessage = null,
  excludeClimbId,
  type,
  boardId,
  boardTypeId,
  inclination,
  trailingAction,
  compactSuggestions = false,
  suggestions: providedSuggestions,
  onExistsChange,
}: ValidatedTitleInputProps) {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [localSuggestions, setLocalSuggestions] = useState<string[]>([]);
  const suggestions = providedSuggestions?.names ?? localSuggestions;
  const manageSuggestions = providedSuggestions?.onManage ?? (() => setDialogOpen(true));

  const {
    validating,
    exists: titleExists,
    errorMessage: validationError,
    validate,
    reset: resetValidation
  } = useTitleValidation({ locationId, type, boardId, boardTypeId, inclination, excludeClimbId });

  // Load suggestions from localStorage — only where the caller has not brought
  // a list of its own.
  useEffect(() => {
    if (providedSuggestions) return;
    setLocalSuggestions(localStorageService.getManualTitles());
  }, [dialogOpen, providedSuggestions]); // Reload when dialog closes

  // Validate title when it changes
  useEffect(() => {
    if (title) {
      validate(title);
    } else {
      resetValidation();
    }
  }, [title, validate, resetValidation]);

  useEffect(() => {
    onExistsChange?.(titleExists);
  }, [titleExists, onExistsChange]);

  function handleSuggestionClick(suggestion: string) {
    onTitleChange(suggestion);
  }

  // Combine validation error with field error (backend error takes precedence)
  const displayError = hasError ? errorMessage : (titleExists ? validationError : null);
  const showError = hasError || titleExists;

  // Get random suggestion that's not the current title
  const currentSuggestion = useMemo(() => {
    const availableSuggestions = title ? suggestions.filter(s => s !== title) : suggestions;
    if (availableSuggestions.length === 0) return null;
    return availableSuggestions[Math.floor(Math.random() * availableSuggestions.length)];
  }, [suggestions, title]);

  return (
    <div className="space-y-2">
      {!compactSuggestions && (
        <div className="flex items-center gap-2">
          <Label htmlFor="title" className="flex-1">
            {t('climbCreate.name')}
          </Label>

          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={manageSuggestions}
            className="h-8 w-8"
            title={t('titleSuggestions.settings')}
          >
            <Settings className="w-4 h-4" />
          </Button>
        </div>
      )}

      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Input
            id="title"
            type="text"
            value={title}
            onChange={(e) => onTitleChange(e.target.value)}
            placeholder={t('climbCreate.name')}
            aria-label={compactSuggestions ? t('climbCreate.name') : undefined}
            aria-invalid={showError || undefined}
            className={`${showError ? 'border-red-500 focus:border-red-500' : ''} ${
              compactSuggestions ? 'pr-20' : ''
            }`}
          />

          {validating && (
            <div
              className={`absolute top-2 text-xs text-muted-foreground ${
                compactSuggestions ? 'right-20' : 'right-2'
              }`}
            >
              {t('common.validating')}...
            </div>
          )}

          {/* Both sat in the field they fill rather than around it: one takes a
              name off the list, the other opens the list. The first is dead when
              there is nothing left to take — every name in it is already this
              one, or there was never a list. */}
          {compactSuggestions && (
            <div className="absolute right-1 top-1/2 flex -translate-y-1/2 items-center">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => currentSuggestion && onTitleChange(currentSuggestion)}
                disabled={!currentSuggestion}
                aria-label={t('titleSuggestions.random')}
                title={t('titleSuggestions.random')}
                className="size-8"
              >
                <Dices className="size-4" aria-hidden />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={manageSuggestions}
                aria-label={t('titleSuggestions.settings')}
                title={t('titleSuggestions.settings')}
                className="size-8"
              >
                <Settings className="size-4" aria-hidden />
              </Button>
            </div>
          )}
        </div>

        {trailingAction}
      </div>

      {showError && displayError && (
        <p className="text-red-500 text-sm">{displayError}</p>
      )}

      {/* A board problem has a board where a climb has a location; either is
          enough to be somewhere a suggested name could go. The compact field
          says the same thing with the button inside it. */}
      {!compactSuggestions && !title && currentSuggestion && (locationId || boardId || boardTypeId) && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{t('titleSuggestions.suggestion')}:</span>
          <button
            type="button"
            onClick={() => handleSuggestionClick(currentSuggestion)}
            className="text-sm text-primary hover:underline cursor-pointer"
          >
            {currentSuggestion}
          </button>
        </div>
      )}

      {/* The browser-local list's own editor. A caller that brought a list
          brought somewhere to manage it too, and this is not it. */}
      {!providedSuggestions && (
        <TitleSuggestionsDialog open={dialogOpen} onOpenChange={setDialogOpen} />
      )}
    </div>
  );
}
